// Iter 138 — `/me/bookings` now shows the customer's NON-waste
// service bookings (doctor / lab / ambulance / home-nursing …) plus a
// "Browse services" call-to-action. Waste pickup bookings have their
// own page at `/me/requests` — this page intentionally does not
// duplicate them.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import { toast } from "sonner";
import {
  Stethoscope, TestTube, Truck, FirstAid, ArrowRight, Calendar,
  Clock, CheckCircle, XCircle, Phone, Sparkle, MapPin,
} from "@phosphor-icons/react";
import { MePageTitle, MeCard, MeSection, MeEmpty, MeButton, MeStagger } from "./MeUI";

const TYPE_META = {
  doctor:       { label: "Doctor consult", Icon: Stethoscope, bg: "bg-[#EEF7EC]", fg: "text-[#DC2626]" },
  lab:          { label: "Lab test",       Icon: TestTube,    bg: "bg-[#EEF2FA]", fg: "text-[#0B2B66]" },
  ambulance:    { label: "Ambulance",      Icon: Truck,       bg: "bg-[#FDE9E4]", fg: "text-[#7A2E18]" },
  home_nursing: { label: "Home nursing",   Icon: FirstAid,    bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]" },
};

const STATUS_TONE = {
  requested:  { label: "Awaiting confirmation", tone: "text-[#7A5A11] bg-[#F9E4B7]" },
  confirmed:  { label: "Confirmed",              tone: "text-[#7F1D1D] bg-[#FEE2E2]" },
  in_progress:{ label: "In progress",            tone: "text-[#0B2B66] bg-[#DDE7F1]" },
  completed:  { label: "Completed",              tone: "text-[#7F1D1D] bg-[#FEE2E2]" },
  cancelled:  { label: "Cancelled",              tone: "text-[#7A2E18] bg-[#F4C7B9]" },
  no_show:    { label: "No show",                tone: "text-[#7A2E18] bg-[#F4C7B9]" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function MeBookings() {
  const [bookings, setBookings] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    custApi.get("/me/health/bookings")
      .then(r => setBookings(r.data.bookings || []))
      .catch(e => {
        setBookings([]);
        toast.error(e?.response?.data?.detail || "Could not load bookings");
      });
  };
  useEffect(() => { load(); }, []);

  const cancel = async (bid) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await custApi.post(`/me/health/bookings/${bid}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const filtered = useMemo(() => {
    if (!bookings) return [];
    if (filter === "all") return bookings;
    if (filter === "upcoming") {
      return bookings.filter(b => ["requested", "confirmed", "in_progress"].includes(b.status));
    }
    if (filter === "past") {
      return bookings.filter(b => ["completed", "cancelled", "no_show"].includes(b.status));
    }
    return bookings.filter(b => b.type === filter);
  }, [bookings, filter]);

  const stats = useMemo(() => {
    if (!bookings) return { total: 0, upcoming: 0, completed: 0, cancelled: 0, next: null };
    const upcoming = bookings.filter(b => ["requested","confirmed","in_progress"].includes(b.status));
    const completed = bookings.filter(b => b.status === "completed");
    const cancelled = bookings.filter(b => ["cancelled","no_show"].includes(b.status));
    const next = upcoming[0]?.scheduled_at ? new Date(upcoming[0].scheduled_at) : null;
    return {
      total: bookings.length,
      upcoming: upcoming.length,
      completed: completed.length,
      cancelled: cancelled.length,
      next,
    };
  }, [bookings]);

  return (
    <div className="font-body" data-testid="me-bookings">
      <MePageTitle
        eyebrow="Rescigo · Bookings"
        title="My Bookings"
        subtitle={<>Doctors, lab tests, ambulance and home-nursing bookings live here. Waste pickups are on <Link to="/me/requests" className="text-[#DC2626] font-bold underline">Requests</Link>.</>}
      />

      {/* Stat strip — 5 cards */}
      <MeSection className="mt-1" testid="me-book-stats">
        <div className="grid grid-cols-5 gap-2">
          <StatPill tone="slate"  label="Total"     value={stats.total}/>
          <StatPill tone="orange" label="Upcoming"  value={stats.upcoming}/>
          <StatPill tone="green"  label="Done"      value={stats.completed}/>
          <StatPill tone="red"    label="Cancelled" value={stats.cancelled}/>
          <StatPill tone="blue"   label="Next"
                    value={stats.next ? stats.next.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                    smallValue={!!stats.next}/>
        </div>
      </MeSection>

      {/* Book more — non-waste service categories */}
      <MeSection testid="me-book-more" className="mt-2">
        <MeCard padding="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-bold">
                Book a new service
              </div>
              <div className="text-[11px] text-[#4B5563] mt-0.5">
                Verified partners near you.
              </div>
            </div>
            <Link to="/me/services" data-testid="me-book-see-all"
                  className="text-[11px] font-bold text-[#DC2626] uppercase tracking-widest">
              See all
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(TYPE_META).map(([key, meta]) => {
              const Icon = meta.Icon;
              return (
                <Link key={key} to={`/me/services/${key}`}
                      data-testid={`me-book-tile-${key}`}
                      className="rounded-2xl bg-white border border-green-900/5 p-3 flex items-center gap-3 active:scale-[0.98] transition-transform">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${meta.bg}`}>
                    <Icon size={20} weight="duotone" className={meta.fg}/>
                  </div>
                  <div className="font-display font-bold text-[13px] text-[#7F1D1D]">{meta.label}</div>
                </Link>
              );
            })}
          </div>
        </MeCard>
      </MeSection>

      {/* Filter chips */}
      <div className="px-5 mt-5 flex items-center gap-1.5 overflow-x-auto pb-1">
        {[
          ["all", "All"], ["upcoming", "Upcoming"], ["past", "Past"],
          ["doctor", "Doctor"], ["lab", "Lab"], ["ambulance", "Ambulance"], ["home_nursing", "Nursing"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)}
                  data-testid={`me-book-filter-${k}`}
                  className={`shrink-0 h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.12em] font-bold transition-colors ${
                    filter === k
                      ? "bg-[#DC2626] text-white shadow-[0_10px_25px_rgba(220,38,38,0.25)]"
                      : "bg-white border border-green-900/5 text-[#4B5563]"
                  }`}>{lbl}</button>
        ))}
      </div>

      {/* Bookings list */}
      <div className="px-5 mt-4">
        {bookings === null ? (
          <div className="text-center text-[#9CA3AF] text-sm py-10">Loading…</div>
        ) : filtered.length === 0 ? (
          <MeEmpty testid="me-book-empty"
            icon={Sparkle}
            title="No bookings yet"
            subtitle="Tap a service above to book your first appointment."
            cta={<MeButton to="/me/services" tone="orange" size="md">Browse services</MeButton>}
          />
        ) : (
          <MeStagger className="space-y-2" stagger={40}>
            {filtered.map(b => <BookingCard key={b.id} b={b} onCancel={() => cancel(b.id)}/>)}
          </MeStagger>
        )}
      </div>
    </div>
  );
}

function BookingCard({ b, onCancel }) {
  const meta = TYPE_META[b.type] || { label: b.type, Icon: Calendar, bg: "bg-[#F7F6F2]", fg: "text-[#5C5A55]" };
  const Icon = meta.Icon;
  const st = STATUS_TONE[b.status] || { label: b.status, tone: "text-[#5C5A55] bg-[#E5E2D9]" };
  const canCancel = ["requested", "confirmed"].includes(b.status);

  // Compute a booking-specific service name from details (varies by type).
  const d = b.details || {};
  let serviceName = "";
  if (b.type === "doctor") {
    serviceName = [d.specialty, d.mode && `(${String(d.mode).replace(/_/g, " ")})`]
      .filter(Boolean).join(" ");
  } else if (b.type === "lab") {
    const tests = Array.isArray(d.tests) ? d.tests : [];
    if (tests.length === 1) serviceName = tests[0]?.name || "Lab test";
    else if (tests.length > 1) serviceName = `${tests[0]?.name || "Lab test"} +${tests.length - 1} more`;
  } else if (b.type === "ambulance") {
    serviceName = d.vehicle_type || d.pickup_type || "Ambulance dispatch";
  } else if (b.type === "home_nursing") {
    serviceName = d.service_type || d.package_name || d.duration_hours
      ? `${d.service_type || "Home nursing"}${d.duration_hours ? ` · ${d.duration_hours}h` : ""}`
      : "Home nursing visit";
  }
  serviceName = (serviceName || "").trim();

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-3 sm:p-4"
         data-testid={`me-book-card-${b.id}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-md flex items-center justify-center ${meta.bg}`}>
          <Icon size={18} weight="duotone" className={meta.fg}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-[14px] text-[#1A1A1A]">{meta.label}</div>
            <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.14em] font-semibold ${st.tone}`}>
              {st.label}
            </span>
            {(b.booking_no || b.no) && (
              <span className="ml-auto text-[10px] font-mono text-[#8A8780]"
                    data-testid={`me-book-no-${b.id}`}>
                #{b.booking_no || b.no}
              </span>
            )}
          </div>
          {serviceName && (
            <div className="text-[13px] font-semibold text-[#1A1A1A] mt-1 capitalize"
                 data-testid={`me-book-service-${b.id}`}>
              {serviceName}
            </div>
          )}
          {b.partner_name && (
            <div className="text-[12px] text-[#5C5A55] mt-0.5"
                 data-testid={`me-book-partner-${b.id}`}>
              {b.partner_name}
            </div>
          )}
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-[11px] text-[#5C5A55]">
            {(b.scheduled_at || b.slot_start) && (
              <div className="inline-flex items-center gap-1">
                <Clock size={11}/>{fmt(b.scheduled_at || b.slot_start)}
              </div>
            )}
            {b.address && (
              <div className="inline-flex items-center gap-1 col-span-2">
                <MapPin size={11}/>{b.address}
              </div>
            )}
            {b.partner_phone && (
              <div className="inline-flex items-center gap-1">
                <Phone size={11}/>
                <a href={`tel:${b.partner_phone}`} className="text-[#DC2626]">{b.partner_phone}</a>
              </div>
            )}
          </div>
          {b.amount ? (
            <div className="text-[12px] mt-1.5 font-mono">
              <span className="text-[#8A8780]">Total </span>
              <span className="text-[#1A1A1A]">₹{Number(b.amount).toFixed(2)}</span>
              {b.payment_status && (
                <span className={`ml-2 text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${
                  b.payment_status === "paid"
                    ? "bg-[#FEE2E2] text-[#7F1D1D]"
                    : "bg-[#F4C7B9] text-[#7A2E18]"
                }`}>{b.payment_status}</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <Link to={`/me/services/${b.type}/booking/${b.id}`}
              data-testid={`me-book-view-${b.id}`}
              className="h-8 px-3 text-[11px] font-medium bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md inline-flex items-center gap-1">
          View details <ArrowRight size={11}/>
        </Link>
        {canCancel && (
          <button onClick={onCancel}
                  data-testid={`me-book-cancel-${b.id}`}
                  className="h-8 px-3 text-[11px] font-medium border border-[#F4C7B9] text-[#7A2E18] rounded-md inline-flex items-center gap-1 hover:bg-[#FDE9E4]">
            <XCircle size={11}/>Cancel
          </button>
        )}
        {b.status === "completed" && (
          <span className="inline-flex items-center gap-1 text-[11px] text-[#7F1D1D]">
            <CheckCircle size={11} weight="fill" color="#DC2626"/>Completed
          </span>
        )}
      </div>
    </div>
  );
}

// ── Stat pill (5-across summary strip) ──────────────────────────
function StatPill({ label, value, tone = "slate", smallValue }) {
  const tones = {
    slate:  "bg-white border-gray-100 text-gray-500",
    orange: "bg-[#FFEDD5] border-transparent text-[#EA580C]",
    green:  "bg-[#DCFCE7] border-transparent text-[#15803D]",
    red:    "bg-[#FEE2E2] border-transparent text-[#DC2626]",
    blue:   "bg-[#DBEAFE] border-transparent text-[#1D4ED8]",
  };
  return (
    <div className={`rounded-2xl border p-2.5 text-center shadow-[0_4px_12px_rgba(0,0,0,0.03)] ${tones[tone]}`}>
      <div className={`font-display font-black leading-none ${smallValue ? "text-[13px]" : "text-xl"}`}>
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-widest font-bold mt-1 opacity-80">{label}</div>
    </div>
  );
}
